<script setup lang="ts">
import { ref, watch } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import type { Group } from '@/models/Group';
import type { UserGroupRole, NewUserGroupRole } from '@/models/GroupRole';
import useAuthStore from '@/store/AuthStore';
import {
  getRoles,
  getGroupRoles,
  patchGroupRole,
  createGroupRole,
  deleteGroupRole,
} from '@/api/GroupRoleApi';
import gettext from '@/i18n/gettext';
import { EMPTY_USER, type User } from '@/models/User';
import type { GroupRole } from '@/models/Roles';

const { $gettext } = gettext;
const authStore = useAuthStore();

const props = defineProps<{ isOpen: boolean; group: Group | null }>();
const emit = defineEmits([
  'update:isOpen',
  'current-user-update',
  'current-user-delete',
]);

const isOpen = ref(props.isOpen);
const manageError = ref('');
const group = ref(props.group);
const userRoles = ref<UserGroupRole[]>([]);
const roles = ref<GroupRole[]>([]);
const newGroupRole = ref<NewUserGroupRole>({
  email: '',
  role: '' as GroupRole,
});
const deleteDialog = ref(false);
const selectedRole = ref<UserGroupRole | null>(null);
const loadingSpinnerStore = useLoadingSpinnerStore();
const user = ref<User>(cloneDeep(EMPTY_USER));

const cancel = () => {
  isOpen.value = false;
  emit('update:isOpen', false);
};

async function loadUser() {
  try {
    await authStore.loadProfileOnce();
    user.value = cloneDeep(authStore.user);
  } catch (error) {
    console.error(error);
  }
}

const loadRoles = async () => {
  try {
    roles.value = await getRoles();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to load group roles:', error);
    manageError.value = error;
  }
};

const loadGroupRoles = async () => {
  if (!group.value) {
    return;
  }
  try {
    userRoles.value = await getGroupRoles(group.value.uuid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to load group roles:', error);
    manageError.value = 'Failed to load group roles';
  }
};

const updateRole = async (groupRole: UserGroupRole, newRole: GroupRole) => {
  if (!group.value) {
    return;
  }
  try {
    manageError.value = '';
    await patchGroupRole(groupRole, group.value.uuid);
    groupRole.role = newRole;
    if (user.value.email === groupRole.email) {
      emit('current-user-update', true);
      cancel();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to update user role:', error);
    manageError.value = 'Failed to update user role';
  }
};

const addRole = async () => {
  if (!newGroupRole.value.email || !newGroupRole.value.role || !group.value) {
    return;
  }

  try {
    manageError.value = '';
    await createGroupRole(newGroupRole.value, group.value.uuid);
    await loadGroupRoles();
    newGroupRole.value = { email: '', role: '' as GroupRole };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to add new user role:', error);
    manageError.value = 'Failed to add new user role';
  }
};

const confirmDelete = (groupRole: UserGroupRole) => {
  selectedRole.value = groupRole;
  deleteDialog.value = true;
};

const deleteRole = async (groupRole: UserGroupRole | null) => {
  if (!groupRole || !group.value) {
    return;
  }

  try {
    await deleteGroupRole(groupRole.email, group.value.uuid);
    deleteDialog.value = false;
    if (user.value.email === groupRole.email) {
      emit('current-user-delete', true);
      cancel();
    } else {
      await loadGroupRoles();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to delete user role:', error);
    manageError.value = 'Failed to delete user role';
  }
};

watch(
  () => props.isOpen,
  async (newVal) => {
    isOpen.value = newVal;
    manageError.value = '';
    if (newVal && props.group) {
      group.value = props.group;
      loadingSpinnerStore.setShowLoadingOverlay(true);
      await loadRoles();
      await loadGroupRoles();
      await loadUser();
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  },
);
</script>

<template>
  <v-dialog v-model="isOpen" max-width="600" @click:outside="cancel">
    <v-card>
      <v-card-title>Manage Group Access</v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item v-for="groupRole in userRoles" :key="groupRole.email">
            <v-row align="center">
              <!-- Name and Email -->
              <v-col cols="6" class="align-center">
                <v-list-item-title>{{ groupRole.name }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  groupRole.email
                }}</v-list-item-subtitle>
              </v-col>
              <!-- Role Select -->
              <v-col cols="4" class="d-flex align-center">
                <v-select
                  v-model="groupRole.role"
                  :items="roles"
                  :label="$gettext('Role')"
                  dense
                  hide-details
                  class="w-100"
                  @update:modelValue="
                    (newRole) => updateRole(groupRole, newRole)
                  "
                ></v-select>
              </v-col>
              <!-- Delete Button -->
              <v-col cols="2" class="d-flex align-center">
                <v-btn icon color="red" @click="confirmDelete(groupRole)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-list-item>
        </v-list>

        <!-- Section to add a new role -->
        <v-row align="center" class="my-2">
          <!-- Email Input -->
          <v-col cols="5" class="d-flex align-center">
            <v-text-field
              v-model="newGroupRole.email"
              :label="$gettext('Email')"
              dense
              hide-details
              class="w-100"
            ></v-text-field>
          </v-col>
          <!-- Role Select -->
          <v-col cols="5" class="d-flex align-center">
            <v-select
              v-model="newGroupRole.role"
              :items="roles"
              :label="$gettext('Role')"
              dense
              hide-details
              class="w-100"
            ></v-select>
          </v-col>
          <!-- Add Button -->
          <v-col cols="2" class="d-flex align-center">
            <v-btn color="primary" dense class="w-100" @click="addRole">{{
              $gettext('Add')
            }}</v-btn>
          </v-col>
        </v-row>

        <!-- Error Alert -->
        <v-alert v-if="manageError" class="mx-2" type="error">
          {{ manageError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn color="blue darken-1" variant="text" @click="cancel">
          {{ $gettext('Close') }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Confirmation Dialog -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="headline">{{
        $gettext('Confirm Deletion')
      }}</v-card-title>
      <v-card-text>{{
        $gettext('Are you sure you want to delete this role?')
      }}</v-card-text>
      <v-card-actions>
        <v-btn variant="text" @click="deleteDialog = false">
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="red" variant="text" @click="deleteRole(selectedRole)">
          {{ $gettext('Delete') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
